import React from 'react';
import {
  IndianRupee, Home, Users, ClipboardList, Sparkles,
  Building2, Receipt, Camera, Globe, UserCheck, UserPlus,
  CalendarCheck, ShieldCheck, MessageSquare, CheckSquare,
  UtensilsCrossed, Brush, Package, UsersRound, BrainCircuit,
  TrendingUp, FileSpreadsheet, Smartphone, Wallet, LayoutGrid,
  BellRing, FileText, Zap, Banknote, Megaphone, Rocket
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Badge = ({ type, t }) => {
  if (type === 'live') {
    return (
      <span data-testid="feature-badge-live" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide bg-green-100 text-green-700 border border-green-200">
        <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
        {t('feat.live_badge')}
      </span>
    );
  }
  if (type === 'ai') {
    return (
      <span data-testid="feature-badge-ai" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 border border-purple-200">
        <Sparkles size={10} />
        {t('feat.ai_badge')}
      </span>
    );
  }
  return (
    <span data-testid="feature-badge-soon" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide bg-amber-50 text-amber-700 border border-amber-200">
      {t('feat.soon_badge')}
    </span>
  );
};

const buildCategories = (t) => ([
  {
    id: 'money',
    title: t('feat.money.title'),
    tagline: t('feat.money.tag'),
    icon: IndianRupee,
    color: 'emerald',
    features: [
      { icon: Wallet, name: t('feat.money.f1'), status: 'live' },
      { icon: Receipt, name: t('feat.money.f2'), status: 'live' },
      { icon: Camera, name: t('feat.money.f3'), status: 'ai' },
      { icon: TrendingUp, name: t('feat.money.f4'), status: 'live' },
      { icon: FileText, name: t('feat.money.f5'), status: 'live' },
      { icon: MessageSquare, name: t('feat.money.f6'), status: 'live' },
      { icon: Banknote, name: t('feat.money.f7'), status: 'live' },
      { icon: TrendingUp, name: t('feat.money.f8'), status: 'new' },
      { icon: Banknote, name: t('feat.money.f9'), status: 'new' },
      { icon: Zap, name: t('feat.money.f10'), status: 'new' }
    ]
  },
  {
    id: 'property',
    title: t('feat.prop.title'),
    tagline: t('feat.prop.tag'),
    icon: Home,
    color: 'blue',
    features: [
      { icon: Building2, name: t('feat.prop.f1'), status: 'live' },
      { icon: LayoutGrid, name: t('feat.prop.f2'), status: 'live' },
      { icon: Camera, name: t('feat.prop.f3'), status: 'live' },
      { icon: Globe, name: t('feat.prop.f4'), status: 'live' },
      { icon: Zap, name: t('feat.prop.f5'), status: 'live' }
    ]
  },
  {
    id: 'tenants',
    title: t('feat.ten.title'),
    tagline: t('feat.ten.tag'),
    icon: Users,
    color: 'indigo',
    features: [
      { icon: UserCheck, name: t('feat.ten.f1'), status: 'live' },
      { icon: BrainCircuit, name: t('feat.ten.f2'), status: 'ai' },
      { icon: CalendarCheck, name: t('feat.ten.f3'), status: 'live' },
      { icon: FileText, name: t('feat.ten.f4'), status: 'live' },
      { icon: ShieldCheck, name: t('feat.ten.f5'), status: 'live' },
      { icon: UserPlus, name: t('feat.ten.f6'), status: 'soon' },
      { icon: CalendarCheck, name: t('feat.ten.f7'), status: 'new' },
      { icon: UserCheck, name: t('feat.ten.f8'), status: 'new' }
    ]
  },
  {
    id: 'ops',
    title: t('feat.ops.title'),
    tagline: t('feat.ops.tag'),
    icon: ClipboardList,
    color: 'orange',
    features: [
      { icon: MessageSquare, name: t('feat.ops.f1'), status: 'live' },
      { icon: CheckSquare, name: t('feat.ops.f2'), status: 'live' },
      { icon: UtensilsCrossed, name: t('feat.ops.f3'), status: 'live' },
      { icon: Brush, name: t('feat.ops.f4'), status: 'live' },
      { icon: Package, name: t('feat.ops.f5'), status: 'live' },
      { icon: UsersRound, name: t('feat.ops.f6'), status: 'live' },
      { icon: Megaphone, name: t('feat.ops.f7'), status: 'live' },
      { icon: UserCheck, name: t('feat.ops.f8'), status: 'live' }
    ]
  },
  {
    id: 'intel',
    title: t('feat.intel.title'),
    tagline: t('feat.intel.tag'),
    icon: Sparkles,
    color: 'purple',
    features: [
      { icon: BrainCircuit, name: t('feat.intel.f1'), status: 'ai' },
      { icon: FileSpreadsheet, name: t('feat.intel.f2'), status: 'live' },
      { icon: BellRing, name: t('feat.intel.f3'), status: 'live' },
      { icon: MessageSquare, name: t('feat.intel.f4'), status: 'soon' },
      { icon: Smartphone, name: t('feat.intel.f5'), status: 'soon' }
    ]
  }
]);

const colorMap = {
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', iconBg: 'bg-emerald-100 text-emerald-600' },
  blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    iconBg: 'bg-blue-100 text-blue-600' },
  indigo:  { bg: 'bg-indigo-50',  text: 'text-indigo-700',  iconBg: 'bg-indigo-100 text-indigo-600' },
  orange:  { bg: 'bg-orange-50',  text: 'text-orange-700',  iconBg: 'bg-orange-100 text-orange-600' },
  purple:  { bg: 'bg-purple-50',  text: 'text-purple-700',  iconBg: 'bg-purple-100 text-purple-600' }
};

const FeaturesSection = () => {
  const { t } = useLanguage();
  const categories = buildCategories(t);
  const totalLive = categories.reduce((s, c) => s + c.features.filter(f => ['live', 'ai', 'new'].includes(f.status)).length, 0);
  const totalNew  = categories.reduce((s, c) => s + c.features.filter(f => f.status === 'new').length, 0);
  const totalSoon = categories.reduce((s, c) => s + c.features.filter(f => f.status === 'soon').length, 0);

  return (
    <section data-testid="features-grid-section" className="py-20 bg-gradient-to-b from-white via-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1 rounded-full text-xs font-semibold mb-4">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
            {t('feat.stats_chip', totalLive, totalSoon)}
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-4">
            {t('feat.h2_a')} <span className="text-blue-600">{t('feat.h2_b')}</span> {t('feat.h2_c')} <span className="text-emerald-600">{t('feat.h2_d')}</span>{t('feat.h2_trail')}
          </h2>
          <p className="text-lg text-gray-600">{t('feat.intro')}</p>
        </div>

        {/* Shipped this month — recently-shipped feature strip */}
        <div data-testid="recently-shipped-strip" className="mb-12">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Rocket size={16} className="text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{t('feat.recently_shipped_title')}</h3>
              <p className="text-xs text-gray-600">{t('feat.recently_shipped_subtitle')}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { icon: TrendingUp, color: 'emerald', title: t('feat.recent.1_title'), desc: t('feat.recent.1_desc') },
              { icon: Zap,        color: 'blue',    title: t('feat.recent.2_title'), desc: t('feat.recent.2_desc') },
              { icon: Banknote,   color: 'amber',   title: t('feat.recent.3_title'), desc: t('feat.recent.3_desc') },
              { icon: CalendarCheck, color: 'purple', title: t('feat.recent.4_title'), desc: t('feat.recent.4_desc') },
            ].map((r, i) => {
              const RIcon = r.icon;
              const toneMap = {
                emerald: 'border-emerald-200 bg-emerald-50/40',
                blue:    'border-blue-200 bg-blue-50/40',
                amber:   'border-amber-200 bg-amber-50/40',
                purple:  'border-purple-200 bg-purple-50/40',
              };
              const iconMap = {
                emerald: 'bg-emerald-100 text-emerald-600',
                blue:    'bg-blue-100 text-blue-600',
                amber:   'bg-amber-100 text-amber-700',
                purple:  'bg-purple-100 text-purple-600',
              };
              return (
                <div
                  key={r.title}
                  data-testid={`recently-shipped-card-${i}`}
                  className={`rounded-xl border ${toneMap[r.color]} p-4 hover:shadow-md transition-shadow`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg ${iconMap[r.color]} flex items-center justify-center flex-shrink-0`}>
                      <RIcon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wide bg-blue-100 text-blue-700 border border-blue-200">
                          <span className="w-1 h-1 bg-blue-500 rounded-full animate-pulse"></span>
                          {t('feat.new_badge')}
                        </span>
                      </div>
                      <h4 className="text-sm font-bold text-gray-900 leading-tight mb-1">{r.title}</h4>
                      <p className="text-xs text-gray-600 leading-snug">{r.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-10">
          {categories.map((cat) => {
            const Icon = cat.icon;
            const colors = colorMap[cat.color];
            return (
              <div key={cat.id} data-testid={`feature-category-${cat.id}`} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow">
                <div className={`flex items-start gap-4 p-6 border-b border-gray-100 ${colors.bg}`}>
                  <div className={`w-12 h-12 rounded-xl ${colors.iconBg} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={24} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-xl font-bold ${colors.text}`}>{cat.title}</h3>
                    <p className="text-sm text-gray-600 mt-0.5 italic">{cat.tagline}</p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-0">
                  {cat.features.map((feat, idx) => {
                    const FeatIcon = feat.icon;
                    return (
                      <div key={feat.name} data-testid={`feature-item-${cat.id}-${idx}`} className="flex items-start gap-3 p-5 border-t border-gray-100 sm:border-t sm:[&:nth-child(-n+2)]:border-t-0 lg:[&:nth-child(-n+3)]:border-t-0 sm:[&:nth-child(odd)]:border-r lg:[&:nth-child(3n+1)]:border-r lg:[&:nth-child(3n+2)]:border-r lg:[&:nth-child(odd)]:border-r-0 sm:border-gray-100 hover:bg-slate-50 transition-colors">
                        <div className={`w-9 h-9 rounded-lg ${colors.iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <FeatIcon size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 leading-snug mb-1.5">{feat.name}</p>
                          <Badge type={feat.status} t={t} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <p data-testid="features-footer-note" className="text-center text-sm text-gray-500 mt-10 max-w-2xl mx-auto">
          {t('feat.footer_note_a')} <span className="font-semibold text-amber-700">{t('feat.soon_badge')}</span> {t('feat.footer_note_b')}
        </p>
      </div>
    </section>
  );
};

export default FeaturesSection;
